import { useEffect, useMemo } from 'react'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useStatsigLoadingStatus } from '@/src/services/Statsig'
import * as Statsig from '../statsig'
import { useStateCode } from '@/src/common/hooks/useMortgageModuleGeoIPBehavior'

const DEFAULT_CONFIG = {
  name: '',
  image: '',
  thumbhash: '',
  enableHomeSection: false,
  enableScheduleACall: false,
  enableInAppLeadForm: false,
} as const satisfies Statsig.Store.MortgageModuleState

export function useStateConfig(stateCodeResult: ReturnType<typeof useStateCode>) {
  const loadingStatus = useStatsigLoadingStatus()
  const mortgageModuleStateConfigParameterStore = Statsig.Store.useMortgageModuleParameterStore()
  const logError = useLogError()

  const result = useMemo(() => {
    try {
      const stateMappingParameter = mortgageModuleStateConfigParameterStore.get('states')

      switch (loadingStatus) {
        case 'Uninitialized':
        case 'Loading':
          return 'loading'
      }

      if (stateCodeResult === 'uninitialized' || stateCodeResult === 'loading') {
        return 'loading'
      }

      if (stateCodeResult === 'error') {
        throw new Error('Failed to obtain state code, hook result was an error')
      }

      const stateCodeNormalized = stateCodeResult.stateCode.toLowerCase()

      if (!stateMappingParameter || !(stateCodeNormalized in stateMappingParameter)) {
        throw new Error(
          `State code ${stateCodeResult.stateCode} not found in state mapping parameter`,
        )
      }

      return Statsig.Store.MortgageModuleStateSchema.safeParse(
        stateMappingParameter[stateCodeNormalized],
      )
    } catch (error) {
      return {
        error: error instanceof Error ? error : new Error('Failed to obtain state config'),
      } as const
    }
  }, [mortgageModuleStateConfigParameterStore, loadingStatus, stateCodeResult])

  useEffect(() => {
    if (result !== 'loading' && 'error' in result) {
      logError(result.error ?? new Error('Failed to obtain state config'), {
        cause: result,
      })
    }
  }, [logError, result])

  if (result === 'loading') {
    return 'loading'
  }

  if ('error' in result) {
    return DEFAULT_CONFIG
  }

  return result.data
}
